import * as THREE from "three";
import gsap from "gsap";

import Experience from "../Experience";
import { couplesRotationTextures } from "../sources";
import { toThreeVector } from "../Utils/aeCoords";

const _worldUp = new THREE.Vector3(0, 1, 0);
const _across = new THREE.Vector3();
const _depth = new THREE.Vector3();
const _up = new THREE.Vector3();
const _camPos = new THREE.Vector3();

// The real turntable: 46 frames packed into one atlas texture (avoids a
// texture upload stall per frame during rotation), sampled by two adjacent
// cells crossfaded via a small custom shader, anchored to the Floor layer's
// baked corners rather than a hand-placed position.
export default class CouplesRotation {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.cameraWrapper = this.experience.camera;
    this.camera = this.experience.camera.instance;
    this.cameraPath = this.experience.camera.cameraPath;
    this.resources = this.experience.resources;
    this.sceneData = this.experience.sceneData;

    this.frameCount = couplesRotationTextures.length;
    // Damped copy of tiltNorm.x, kept in the same -1..1 range — the sign
    // and magnitude driving which frame updateCells() selects, radiating
    // out from the centre frame (see updateCells for the full mapping).
    this.rotationAngle = 0;

    this.setTurntableConfig();
    this.init();
  }

  setTurntableConfig() {
    const tt = this.sceneData.preset.turntable;
    this.tt = {
      unpin: tt.tt_unpin,
      matchTilt: tt.tt_matchTilt,
      gate: tt.tt_gate,
      invert: tt.tt_invert,
      damp: Number(tt.tt_damp) / 100,
      minSpan: Number(tt.tt_minSpan),
      maxSpan: Number(tt.tt_maxSpan),
      shiftX: Number(tt.tt_shiftX),
      shiftY: Number(tt.tt_shiftY),
      shiftZ: Number(tt.tt_shiftZ),
      size: Number(tt.tt_size),
      bindIndex: Number(tt.ttBind),
      // 1-based centre frame from preset (ttCentre), converted to a
      // 0-based array index.
      centerIndex: Math.max(0, Number(tt.ttCentre) - 1),
      nearClip: tt.ttNear || "dolly",
      // Per-cell pixel cap (ttCell) — the source frames are 1638x1638, so
      // this is a real quality cap, not just a memory guard. Home ships
      // 1536; still clamped against the GPU's maxTextureSize below.
      cellCap: Number(tt.ttCell) || 1024,
    };
  }

  async init() {
    // Exposed as this.ready — World/Experience await it (alongside
    // TextCard's own async font-driven init) before calling
    // renderer.compile(), so the shader link + atlas texture upload happen
    // on this invisible mesh well before showCouplesRotation() first flips
    // it visible, instead of stalling that reveal frame.
    this.ready = this.loadImages().then(() => {
      this.buildAtlas();
      this.createMesh();
    });
    await this.ready;
  }

  async loadImages() {
    const pending = couplesRotationTextures.map(
      (path) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.src = path;
        }),
    );

    this.images = await Promise.all(pending);
  }

  // Packs every frame into a single square-ish grid canvas so the whole
  // turntable is one texture upload, not 46 separate ones swapped per tick.
  // Exact port of the reference's ttPack: a cell can never exceed the
  // source image, the preset's cell cap (ttCell: 1536), or what the grid
  // allows inside the GPU's own maxTextureSize.
  //
  // This was wrongly suspected (and capped down to 512) during an earlier
  // FPS investigation — the real cause turned out to be an unrelated
  // per-tick full-texture GPU re-upload in SequenceBG.applyCoverFitUV (see
  // its comment), confirmed by profiling with the atlas already shrunk and
  // the drop persisting unchanged. Restored to the reference's real ttCell
  // value now that the actual cause is fixed.
  buildAtlas() {
    const first = this.images[0];
    const cols = Math.ceil(Math.sqrt(this.frameCount));
    const rows = Math.ceil(this.frameCount / cols);
    const aspect = first.width / first.height;

    const maxDim = Math.min(
      this.experience.renderer?.instance?.capabilities.maxTextureSize || 4096,
      8192,
    );

    let cellW = Math.min(this.tt.cellCap, first.width, Math.floor(maxDim / cols));
    let cellH = Math.round(cellW / aspect);
    if (rows * cellH > maxDim) {
      cellH = Math.floor(maxDim / rows);
      cellW = Math.round(cellH * aspect);
    }
    cellW = Math.max(2, cellW);
    cellH = Math.max(2, cellH);

    const canvas = document.createElement("canvas");
    canvas.width = cellW * cols;
    canvas.height = cellH * rows;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    this.images.forEach((img, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      ctx.drawImage(img, col * cellW, row * cellH, cellW, cellH);
    });

    this.atlasCols = cols;
    this.atlasRows = rows;
    this.atlasCellW = cellW;
    this.atlasCellH = cellH;
    // Source frame's own aspect — the mesh is sized h = tt.size,
    // w = h * atlasAspect (see update()), not a square plane.
    this.atlasAspect = aspect;

    this.atlasTexture = new THREE.CanvasTexture(canvas);
    this.atlasTexture.colorSpace = THREE.SRGBColorSpace;
    this.atlasTexture.wrapS = THREE.ClampToEdgeWrapping;
    this.atlasTexture.wrapT = THREE.ClampToEdgeWrapping;
    this.atlasTexture.generateMipmaps = false;
    this.atlasTexture.minFilter = THREE.LinearFilter;
    this.atlasTexture.magFilter = THREE.LinearFilter;
    if (this.experience.renderer?.instance) {
      // Capped, not maxed — see the matching comment in SequenceBG.loadFrame
      // for why (sampling cost scales with the anisotropy level; 8x keeps
      // nearly all the visible benefit at a fraction of the cost).
      this.atlasTexture.anisotropy = Math.min(
        8,
        this.experience.renderer.instance.capabilities.getMaxAnisotropy(),
      );
    }
  }

  cellOffset(index) {
    // Clamp, not wrap — tilting fully to one side should land on the first
    // or last captured frame and stay there, not spin back around.
    const i = THREE.MathUtils.clamp(index, 0, this.frameCount - 1);
    const col = i % this.atlasCols;
    const row = Math.floor(i / this.atlasCols);
    return [col / this.atlasCols, 1 - (row + 1) / this.atlasRows];
  }

  createMesh() {
    // Half-texel inset (in atlas UV space) so bilinear filtering never
    // bleeds into the neighbouring atlas cell at a frame's edge. Half a
    // pixel of the actual cell size the canvas was packed at, not a
    // hardcoded guess — buildAtlas() may pick a smaller cell than 512 on a
    // GPU with a tight maxTextureSize.
    const insetU = 0.5 / (this.atlasCellW * this.atlasCols);
    const insetV = 0.5 / (this.atlasCellH * this.atlasRows);

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      uniforms: {
        uAtlas: { value: this.atlasTexture },
        uCellSize: { value: new THREE.Vector2(1 / this.atlasCols, 1 / this.atlasRows) },
        uInset: { value: new THREE.Vector2(insetU, insetV) },
        uOffA: { value: new THREE.Vector2() },
        uOffB: { value: new THREE.Vector2() },
        uMix: { value: 0 },
        uOpacity: { value: 0 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D uAtlas;
        uniform vec2 uCellSize;
        uniform vec2 uInset;
        uniform vec2 uOffA;
        uniform vec2 uOffB;
        uniform float uMix;
        uniform float uOpacity;
        varying vec2 vUv;

        void main() {
          vec2 local = clamp(vUv, uInset / uCellSize, 1.0 - uInset / uCellSize) * uCellSize;
          vec4 a = texture2D(uAtlas, uOffA + local);
          vec4 b = texture2D(uAtlas, uOffB + local);
          vec4 c = mix(a, b, uMix);
          gl_FragColor = vec4(c.rgb, c.a * uOpacity);
        }
      `,
    });

    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.material);
    this.mesh.visible = false;
    this.scene.add(this.mesh);

    this.updateAnchor();
    this.cacheProximityBounds();
    this.updateCells();
  }

  // Position/size/orientation basis from the Floor layer's baked corners —
  // this is what makes the turntable land at the right AE-comp-scale place
  // instead of a hand-guessed position.
  updateAnchor() {
    const floor = this.sceneData.scene.layers[this.tt.bindIndex];
    const compW = this.cameraPath.compWidth;
    const compH = this.cameraPath.compHeight;
    const frame = floor.frames[0];

    const tl = toThreeVector(frame.tl, compW, compH);
    const tr = toThreeVector(frame.tr, compW, compH);
    const bl = toThreeVector(frame.bl, compW, compH);

    // ex = across the layer, down = into it — matches applyFrame's own
    // basis so "centre" means the same thing here as it does for the
    // layer's own quad (tl + half ex + half down).
    const ex = tr.clone().sub(tl);
    const down = bl.clone().sub(tl);
    const centre = tl.clone().addScaledVector(ex, 0.5).addScaledVector(down, 0.5);

    _across.copy(ex).normalize();
    _depth.copy(down).normalize();
    _up.crossVectors(_depth, _across).normalize();
    if (_up.dot(_worldUp) < 0) _up.negate();

    this.anchor = centre
      .addScaledVector(_across, this.tt.shiftX)
      .addScaledVector(_up, this.tt.shiftY)
      .addScaledVector(_depth, this.tt.shiftZ);
  }

  showCouplesRotation() {
    this.mesh.visible = true;
    gsap.to(this.material.uniforms.uOpacity, {
      value: 1,
      duration: 1,
      ease: "power2.out",
    });
  }

  // Camera's baked (pre-tilt) position at an arbitrary timeline frame —
  // read from the bake so proximity never drifts with live tilt. Returns a
  // shared scratch vector (copy out before the next call) rather than
  // allocating — this runs every tick via proximity().
  camPosAtFrame(frame) {
    const frames = this.cameraPath.frames;
    const rel = THREE.MathUtils.clamp(frame, 0, frames.length - 1);
    const i0 = Math.floor(rel);
    const i1 = Math.min(i0 + 1, frames.length - 1);
    const t = rel - i0;
    return _camPos.copy(frames[i0].p).lerp(frames[i1].p, t);
  }

  // near/far are both invariant once the anchor is known (near = distance
  // at the nearClip's fixed midpoint, far = distance at frame 0) — cached
  // once here instead of recomputed from a fresh camPosAtFrame() allocation
  // every tick inside proximity(), which runs unconditionally the whole
  // scene long.
  cacheProximityBounds() {
    const clip = this.sceneData.clips.find((c) => c.id === this.tt.nearClip);
    if (!clip) {
      this.proximityBounds = null;
      return;
    }

    const nearF = (clip.from + clip.to) / 2;
    this.proximityBounds = {
      near: this.camPosAtFrame(nearF).distanceTo(this.anchor),
      far: this.camPosAtFrame(0).distanceTo(this.anchor),
    };
  }

  // 0 (far) .. 1 (near) — how close the baked camera is to the turntable
  // at the current playhead, relative to its distance at the nearClip
  // clip's midpoint (nearest) vs. the very first frame (farthest).
  proximity(playheadFrame) {
    if (!this.proximityBounds || !this.anchor) return 1;

    const { near, far } = this.proximityBounds;
    const current = this.camPosAtFrame(playheadFrame).distanceTo(this.anchor);

    if (far - near < 1) return 1;
    return THREE.MathUtils.clamp((far - current) / (far - near), 0, 1);
  }

  // Exact port of the reference's updateTurntable cell-selection: frames
  // radiate outward from a centre frame (ttCentre), reach bounded by a
  // span interpolated between minSpan (far) and maxSpan (near) by camera
  // proximity — NOT a plain 0..1 sweep across the whole atlas, so the
  // turn neither wraps nor spans more of the sequence than the reach
  // allows.
  updateCells(playheadFrame) {
    const n = this.frameCount;
    const c = this.tt.centerIndex;

    const prox = this.proximity(playheadFrame ?? 0);
    const fullSpan = Math.max(n - 1 - c, c);
    const nearSpan = this.tt.maxSpan > 0 ? Math.min(this.tt.maxSpan, fullSpan) : fullSpan;
    const farSpan = Math.min(this.tt.minSpan, nearSpan);
    const span = THREE.MathUtils.lerp(farSpan, nearSpan, prox);

    const sgn = THREE.MathUtils.clamp(this.rotationAngle, -1, 1) * (this.tt.invert ? -1 : 1);
    const reach = sgn >= 0 ? Math.min(span, n - 1 - c) : Math.min(span, c);
    const fi = THREE.MathUtils.clamp(c + sgn * reach, 0, n - 1);

    const i0 = Math.floor(fi);
    const mix = fi - i0;

    const [uA, vA] = this.cellOffset(i0);
    const [uB, vB] = this.cellOffset(i0 + 1);

    this.material.uniforms.uOffA.value.set(uA, vA);
    this.material.uniforms.uOffB.value.set(uB, vB);
    this.material.uniforms.uMix.value = mix;
  }

  update(dt, playheadFrame) {
    if (!this.mesh) return;

    this.mesh.position.copy(this.anchor);
    // tt_unpin: face the baked (pre-tilt) camera orientation, not the live
    // tilted one — keeps the turntable from swimming with pointer tilt.
    this.mesh.quaternion.copy(
      this.tt.unpin ? this.cameraPath.bakedQuat : this.camera.quaternion,
    );

    const h = this.tt.size;
    this.mesh.scale.set(h * (this.atlasAspect || 1), h, 1);

    if (this.tt.matchTilt) {
      // tiltNorm.x is already normalized -1..1 across the full soft-limited
      // tilt range. Gated through tiltGain so the turn opens up over the
      // same ramp the camera tilt does, rather than switching on at once.
      let drive = this.cameraWrapper.tiltNorm.x;
      if (this.tt.gate) drive *= this.cameraWrapper.tiltGain;

      const k = 1 - Math.pow(1 - this.tt.damp, dt * 60);
      this.rotationAngle += (drive - this.rotationAngle) * k;
      this.updateCells(playheadFrame);
    }
  }
}
